import { useState } from "react";
import { Hint } from "@/ui/Hint";
import {
  Button,
  Flex,
  IconButton,
  Input,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { LuSearch, LuX } from "react-icons/lu";

import { useCreateProductKit } from "../useCases/useCreateProductKit";
import { Table } from "@/ui/tables/Table";
import type { TableProps } from "@/ui/tables/Table";
import { ModalSearchProduct } from "@/core/products/components/ModalSearchProduct";

import type { ProductState } from "@/core/products/components/ProductsTable";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import {
  CreateProductKitInput,
  createProductKitSchema,
} from "../schemas/productKitSchema";
import { useGetStorage } from "@/core/storages/useCases/useGetStorage";
import { useAuth } from "@/shared/session";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { Field } from "@/ui/chakra/field";
import { NativeSelectField, NativeSelectRoot } from "@/ui/chakra/native-select";
import { toast } from "@/ui/chakra/toaster";

interface CreateModalProductKitProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateModalProductKit = ({
  isOpen,
  onClose,
}: CreateModalProductKitProps) => {
  const [isErpModalOpen, setErpModalOpen] = useState(false);
  const { mutate: createKit, isPending } = useCreateProductKit();

  const { session } = useAuth();
  const { data: storages } = useGetStorage(session?.zone ?? "");
  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm<CreateProductKitInput>({
    resolver: zodResolver(createProductKitSchema),
    defaultValues: {
      name: "",
      products: [],
      enabled: true,
      storageId: "",
    },
  });

  const { products, storageId } = watch();

  const handleAddProduct = (product: ProductState) => {
    if (products.some((p) => p.data.id === product.data.id)) return;
    setValue("products", [...products, product]);
  };

  const handleRemoveProduct = (productId: string) => {
    setValue(
      "products",
      products.filter((product) => product.data.id !== productId),
    );
  };

  const onFormSubmit = (data: CreateProductKitInput) => {
    const formattedProducts = data.products.map((product) => ({
      id: product.data.id,
      quantity: product.amount,
    }));

    createKit(
      {
        ...data,
        products: formattedProducts,
      },
      {
        onSuccess: () => {
          resetFields();
          toast({
            title: "Kit de productos creado",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          onClose();
        },
        onError: () => {
          toast({
            title: "Error al crear el kit",
            description:
              "Ocurrió un error al intentar crear el kit. Por favor, inténtalo de nuevo.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        },
      },
    );
  };

  const getSelectedProductsColumns =
    (): TableProps<ProductState>["headers"] => [
      {
        label: "Producto",
        itemLabel: (item) => item.data.description,
      },
      {
        label: "Almacén",
        itemLabel: (item) => item.data.storage.name,
      },
      {
        label: "Cantidad",
        align: "end",
        itemLabel: (item) => (
          <Input
            size="sm"
            type="number"
            w="90px"
            textAlign="end"
            aria-label="Cantidad"
            value={item.amount}
            onChange={(e) =>
              setValue(
                "products",
                products.map((p) =>
                  p.data.id === item.data.id
                    ? {
                        ...p,
                        amount: Math.max(1, Number(e.target.value)),
                      }
                    : p,
                ),
              )
            }
            min={1}
          />
        ),
      },
      {
        label: "",
        align: "end",
        itemLabel: (item) => (
          <IconButton
            aria-label="Quitar producto"
            size="sm"
            variant="ghost"
            color="fg.muted"
            onClick={() => handleRemoveProduct(item.data.id)}
          >
            <LuX />
          </IconButton>
        ),
      },
    ];

  const handleOnClose = () => {
    onClose();
    resetFields();
  };

  const resetFields = () => {
    reset();
  };

  return (
    <>
      <DialogRoot isOpen={isOpen} onClose={handleOnClose} size="xl" isCentered>
        <DialogContent maxW="5xl">
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <DialogHeader borderBottomWidth="1px" borderColor="border">
              <DialogTitle>Crear kit de productos</DialogTitle>
            </DialogHeader>
            <DialogCloseTrigger />
            <DialogBody display="flex" flexDirection="column" gap={6} pt={5}>
              <Hint>
                Un kit agrupa productos con sus cantidades para agregarlos a un
                vale de una sola vez, desde el botón «Código kit».
              </Hint>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                <Field
                  label="Nombre del kit"
                  required
                  invalid={!!errors.name}
                  errorText={errors.name?.message}
                >
                  <Input {...register("name")} placeholder="Nombre del kit" />
                </Field>
                <Field
                  label="Bodega"
                  required
                  invalid={!!errors.storageId}
                  errorText={errors.storageId?.message?.toString()}
                >
                  <Controller
                    name="storageId"
                    control={control}
                    render={({ field }) => (
                      <NativeSelectRoot>
                        <NativeSelectField
                          placeholder="Seleccione una bodega"
                          {...field}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            setValue("products", []);
                          }}
                        >
                          {storages?.map((storage) => (
                            <option key={storage.id} value={storage.id}>
                              {storage.name}
                            </option>
                          ))}
                        </NativeSelectField>
                      </NativeSelectRoot>
                    )}
                  />
                </Field>
              </SimpleGrid>

              <Field
                invalid={!!errors.products}
                errorText={errors.products?.message}
              >
                <Flex
                  align="center"
                  justify="space-between"
                  gap={4}
                  wrap="wrap"
                  w="full"
                  mb={3}
                >
                  <Text fontWeight="semibold">
                    Productos{" "}
                    <Text as="span" color="fg.muted" fontWeight="normal">
                      · {products.length}
                    </Text>
                  </Text>
                  <Button
                    variant="outline"
                    fontWeight="normal"
                    color="fg.muted"
                    justifyContent="flex-start"
                    w={{ base: "full", sm: "320px" }}
                    onClick={() => setErpModalOpen(true)}
                    type="button"
                  >
                    <LuSearch />
                    Agregar producto...
                  </Button>
                </Flex>
                <Table
                  headers={getSelectedProductsColumns()}
                  data={products}
                  extractItemKey={(item) => item.data.id}
                  showPagination={false}
                  showConfigColumns={false}
                  showSearch={false}
                  showDefaultActions={false}
                />
              </Field>
            </DialogBody>
            <DialogFooter borderTopWidth="1px" borderColor="border">
              <Button variant="outline" onClick={handleOnClose} type="button">
                Cancelar
              </Button>
              <Button colorPalette="brand" type="submit" loading={isPending}>
                Crear kit
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </DialogRoot>

      <ModalSearchProduct
        isOpen={isErpModalOpen}
        onClose={() => setErpModalOpen(false)}
        selected={products}
        storageId={storageId}
        onChange={(product) => handleAddProduct(product)}
      />
    </>
  );
};

export default CreateModalProductKit;
